/**
 * computeDailyExpenses — pure helper extracted from coreSlice.nextDay
 * (03/06/2026 round 2 refactor of the daily P&L block).
 *
 * Returns the day expenses + the monthly cost breakdown used downstream
 * by `expenseBreakdown` (TVA tracking) and by `monthlyReport`.
 *
 * The caller is responsible for applying the difficulty multiplier on
 * top of the returned `dayExpenses` and for adding the accountancy
 * cost, the export-abroad cost, the DREAL fines, and the agios fees
 * (all of which sit outside this helper).
 *
 * Side-effects: none.
 *
 * @param {Object} state - the current game state snapshot
 * @param {boolean} working - true when isWorkingDay(state.day)
 * @param {Object} opts
 * @param {number} opts.supportMaintenanceDiscount - cumulative
 *   maintenance discount from hired support (0..0.7)
 * @param {number} opts.supportFuelDiscount - cumulative fuel discount
 *   from hired support
 * @param {number} opts.supportMonthlyCost - total monthly cost of
 *   hired support roles
 * @param {number} opts.officeMonthlyCost - total monthly cost of
 *   office subscriptions
 * @param {number} opts.monthlyLeaseRent - net monthly rent (depots +
 *   office) after any regional aids
 * @returns {{
 *   dayExpenses: number,
 *   monthlyFixed: number,
 *   monthlyRH: number,
 *   monthlyMaintenance: number,
 *   monthlyInsurance: number,
 *   monthlyInvestments: number,
 *   monthlyLeasing: number,
 *   monthlyCredit: number,
 * }}
 */
import { INVESTMENTS } from '../../data/investments';
import { DRIVERS } from '../../data/drivers';
import { getTotalDepotRent } from '../../data/depotGeo';
import { getTotalManagerCost } from '../../data/depotManagers';
import { getDepotEffectiveInvestments } from '../../data/depotPhases';
import { getManagerMaintenanceMultiplier, getManagerFuelMultiplier } from '../../utils/managerBonuses';
import { findCityById, haversineKm } from '../../data/mapCities';
import { deriveContractDestination } from '../../utils/mapRoutes';

export function computeDailyExpenses(state, working, opts = {}) {
  const supportMaintenanceDiscount = opts.supportMaintenanceDiscount || 0;
  const supportFuelDiscount = opts.supportFuelDiscount || 0;
  const monthlySupport = opts.supportMonthlyCost || 0;
  const monthlyOffice = opts.officeMonthlyCost || 0;
  const monthlyLeaseRent = opts.monthlyLeaseRent || 0;

  // Resolution effects (Conseil d'administration) — expire after 360 days.
  const res = state.activeResolution;
  const resolutionActive = res && (res.expiresAt === undefined || state.totalDays < res.expiresAt);
  const resMaintenanceDiscount = (resolutionActive && res?.maintenanceDiscount) || 0;
  const resFuelDiscount = (resolutionActive && res?.fuelDiscount) || 0;
  const resSalaryDiscount = (resolutionActive && res?.salaryDiscount) || 0;

  const monthlyFixed = 250 + 75 + 25;
  const monthlyRH = state.drivers.reduce((sum, d) => {
    const driverData = DRIVERS.find(dd => dd.id === d.dataId);
    return sum + (driverData?.employerCost || 0);
  }, 0) * (1 - resSalaryDiscount);
  // 29/05/2026 — audit credit fix : filter out fully repaid loans
  // (remainingMonths <= 0).
  const monthlyCredit = state.credits.reduce((sum, c) => {
    if ((c.remainingMonths || 0) <= 0) return sum;
    // 18/06/2026 — geste commercial constructeur : mensualité(s) offerte(s)
    // (freeMonthsRemaining > 0) → aucun débit joueur ce mois-ci.
    if ((c.freeMonthsRemaining || 0) > 0) return sum;
    return sum + c.monthlyPayment;
  }, 0);
  // 02/06/2026 — defensive `|| 0` to harden against vehicles missing
  // `maintenancePerMonth` (acquired competitors, legacy saves).
  const monthlyMaintenanceRaw = state.vehicles.reduce((sum, v) => {
    let cost = v.maintenancePerMonth || 0;
    // 13/06/2026 — Chantier Dépôts : atelier mécanique local (−10 % sur la
    // maintenance des véhicules rattachés à un dépôt équipé).
    // 13/07/2026 — Progression unifiée : l'atelier d'un dépôt secondaire =
    // investissement `inv-depot-12` (legacy `atelier` mappé automatiquement).
    if (v.depotId) {
      const depot = (state.ownedDepots || []).find(d => d.id === v.depotId);
      if (depot && !depot.isHeadquarters && getDepotEffectiveInvestments(depot)['inv-depot-12']) cost *= 0.9;
    }
    // 17/06/2026 — Bonus manager `maintenance` (Thierry Mendes) : −7 % sur
    // les camions du dépôt qu'il dirige. Cf. utils/managerBonuses.js.
    cost *= getManagerMaintenanceMultiplier(v, state.ownedDepots, state.hiredDepotManagers);
    return sum + cost;
  }, 0);
  const totalMaintDiscount = Math.min(0.7, supportMaintenanceDiscount + resMaintenanceDiscount);
  const monthlyMaintenance = monthlyMaintenanceRaw * (1 - totalMaintDiscount);
  // Insurance premium varies with coverage tier (basic 0.70 / premium 1.35).
  const monthlyInsurance = state.vehicles.reduce((sum, v) => {
    const tier = v.insuranceCoverage || 'standard';
    const mult = tier === 'basic' ? 0.70 : tier === 'premium' ? 1.35 : 1;
    return sum + (v.insurancePerMonth || 0) * mult;
  }, 0);
  const monthlyInvestments = Object.keys(state.activeInvestments).reduce((sum, invId) => {
    const inv = INVESTMENTS.find(i => i.id === invId);
    return sum + (inv?.chargeMonthly || 0);
  }, 0);
  const monthlyLeasing = state.vehicles.reduce((sum, v) => sum + (v.isLeased ? (v.leaseMonthlyPayment || 0) : 0), 0);
  // 13/06/2026 — Chantier Dépôts Phase 1 : loyers par ville (grille A/B/C/EU)
  // évoluant avec les investissements (taille du terrain), avec aides à
  // l'implantation (QG exonéré 90 j, satellites −50 % pendant 180 j).
  const monthlyDepots = getTotalDepotRent(
    state.ownedDepots || [],
    state.activeInvestments || {},
    state.company,
    state.totalDays || 0,
  );

  // 17/06/2026 — Refonte dépôts : salaires des responsables de site (1 par
  // dépôt non-QG, ~3 200 à 5 200 €/mois selon séniorité).
  const monthlyManagers = getTotalManagerCost(state.ownedDepots || []);

  let dayExpenses = (monthlyFixed + monthlyRH + monthlyCredit + monthlyMaintenance + monthlyInsurance + monthlyInvestments + monthlyLeasing + monthlySupport + monthlyDepots + monthlyManagers + monthlyOffice + monthlyLeaseRent) / 22;

  // Fuel: 100% if on active mission, 5% idle stipend otherwise (engine
  // starts, anti-corrosion runs, depot manoeuvres).
  // 02/06/2026 — defensive `|| 0` to harden against any vehicle that
  // joined the fleet without a `dailyFuel` field (acquired competitors,
  // legacy saves). Prevents NaN propagation into treasury.
  // 14/06/2026 — borne de recharge électrique (évolution Tier 5) : −15 %
  // de carburant/énergie pour les véhicules rattachés à un dépôt équipé.
  const vehicleFuelToday = (v) => {
    let fuel = v.dailyFuel || 0;
    // 13/07/2026 — Progression unifiée : la pompe/borne d'un dépôt
    // secondaire = investissement `inv-energy-02` (legacy `borne` mappé)
    // → −15 % carburant/énergie pour les véhicules rattachés au site.
    if (v.depotId) {
      const depot = (state.ownedDepots || []).find(d => d.id === v.depotId);
      if (depot && !depot.isHeadquarters && getDepotEffectiveInvestments(depot)['inv-energy-02']) fuel *= 0.85;
    }
    // 17/06/2026 — Bonus manager `fuel` (Anouk Vandersteen) : −5 % sur
    // la conso des camions de son dépôt.
    fuel *= getManagerFuelMultiplier(v, state.ownedDepots, state.hiredDepotManagers);
    return (v.status === 'active' || v.assignedContractId) ? fuel : fuel * 0.05;
  };
  if (working) {
    state.vehicles.forEach(v => {
      dayExpenses += vehicleFuelToday(v);
    });
  }

  // 09/02/2026 — Q2 Lot I user prod : ajout des PÉAGES autoroutiers.
  // Calcul par contrat actif : distance × 0.08 €/km (moyenne France
  // autoroutes payantes incluant les portions gratuites). Plafonné à
  // 50 €/jour/véhicule (le réseau péager dense des grands axes ne
  // dépasse pas ce coût en pratique pour un PL hors convois lourds).
  // Missions < 30 km ignorées (urbain/régional sans autoroute).
  let dailyToll = 0;
  if (working && Array.isArray(state.activeContracts)) {
    state.activeContracts.forEach(c => {
      const v = state.vehicles.find(vv => vv.id === c.vehicleId);
      if (!v) return;
      const isActive = v.status === 'active' || v.assignedContractId === c.instanceId;
      if (!isActive) return;
      const km = c.distanceKm || 0;
      if (km < 30) return;
      let toll = Math.min(50, km * 0.08);
      // Tier 5+ (convois exceptionnels) : tarif majoré ×1.5.
      if ((v.tier || 1) >= 5) toll *= 1.5;
      dailyToll += toll;
    });
  }
  dayExpenses += dailyToll;

  // 06/02/2026 — P2 retour user : choc carburant réel (news-fuel-up-01 / down-01).
  // Le modifier persiste pendant `fuelPriceModifier.expiresOnDay - totalDays` j.
  // Hausse +5 % (multiplier 1.05) ou baisse −3 % (multiplier 0.97).
  const fpm = state.fuelPriceModifier;
  const fpmActive = working && fpm && Number.isFinite(fpm.expiresOnDay) && state.totalDays < fpm.expiresOnDay;
  if (fpmActive) {
    const fuelPortion = state.vehicles.reduce((sum, v) => sum + vehicleFuelToday(v), 0);
    // Le delta est appliqué après les remises (multiplicateur supplémentaire
    // sur le carburant brut). On considère que les remises support/résolution
    // ne protègent pas du choc de marché.
    dayExpenses += fuelPortion * (fpm.multiplier - 1);
  }

  // 11/02/2026 (Phase 3 — scénario formateur pushé) — Choc pétrolier
  // pédagogique. Se cumule avec un fuelPriceModifier de news existant.
  // Structure : state.fuelShock = { multiplier, endDay }.
  const shock = state.fuelShock;
  const shockActive = working && shock && Number.isFinite(shock.endDay) && state.totalDays <= shock.endDay;
  if (shockActive) {
    const fuelPortion = state.vehicles.reduce((sum, v) => sum + vehicleFuelToday(v), 0);
    dayExpenses += fuelPortion * (shock.multiplier - 1);
  }

  // 06/02/2026 — P2 retour user : grève péages bloqués ⇒ surcoût détours
  // quotidien fixe (péage alternatif + km en plus) pendant N jours. Pas
  // d'impact sur les contrats — on facture le détour en charges.
  const strike = state.peageStrike;
  const strikeActive = working && strike && Number.isFinite(strike.expiresOnDay) && state.totalDays < strike.expiresOnDay;
  if (strikeActive) {
    const activeCount = state.vehicles.filter(v => v.status === 'active' || v.assignedContractId).length;
    dayExpenses += activeCount * (strike.dailyCostPerVehicle || 0);
  }

  const fuelDiscount = Object.keys(state.activeInvestments).reduce((acc, invId) => {
    const inv = INVESTMENTS.find(i => i.id === invId);
    if (inv && inv.impactMultiplier < 0) return acc + Math.abs(inv.impactMultiplier);
    return acc;
  }, 0);
  const totalFuelDiscount = fuelDiscount + supportFuelDiscount + resFuelDiscount;
  if (totalFuelDiscount > 0) {
    const fuelPortion = state.vehicles.reduce((sum, v) => sum + vehicleFuelToday(v), 0);
    dayExpenses -= fuelPortion * Math.min(totalFuelDiscount, 0.5);
  }

  // HQ ZONE BONUS — −10 % carburant pour les contrats dont la destination
  // dérivée est dans un rayon de 200 km autour de la ville HQ. Ne
  // s'applique qu'aux véhicules effectivement en mission.
  if (working && state.company?.city && state.activeContracts?.length) {
    const hq = findCityById(state.company.city);
    if (hq) {
      let localFuelTotal = 0;
      state.activeContracts.forEach(c => {
        const dest = deriveContractDestination(c, state.company.city, state.phase || 1);
        if (!dest) return;
        if (haversineKm(hq, dest) > 200) return;
        const v = state.vehicles.find(vv => vv.id === c.vehicleId);
        if (v && (v.status === 'active' || v.assignedContractId)) {
          localFuelTotal += (v.dailyFuel || 0);
        }
      });
      if (localFuelTotal > 0) {
        dayExpenses -= localFuelTotal * 0.10;
      }
    }
  }

  return {
    dayExpenses,
    monthlyFixed,
    monthlyRH,
    monthlyMaintenance,
    monthlyInsurance,
    monthlyInvestments,
    monthlyLeasing,
    monthlyCredit,
    // 09/02/2026 — Q2 Lot I : exposé pour breakdown / monthlyReport.
    dailyToll,
    // 22/07/2026 — exposés pour le détail des charges par poste.
    monthlyDepots,
    monthlyManagers,
  };
}
